import React, { useState } from 'react';
import RideAppIcon from './RideAppIcon';
import RidePlaceSearchSheet from './RidePlaceSearchSheet';
import { RideScheduleSheet, RideSeatSheet } from './RideSearchOptionSheets';

const RideSearchSection = ({
  loading,
  onScheduleRide,
  externalPickup,
  externalDropoff,
  onOpenMapPicker,
}) => {
  const [pickup, setPickup] = useState(externalPickup || null);
  const [dropoffObj, setDropoffObj] = useState(externalDropoff || null);
  const [departureAt, setDepartureAt] = useState(null);
  const [rideSeats, setRideSeats] = useState(1);
  const [editingTarget, setEditingTarget] = useState(null); // 'PICKUP' | 'DROPOFF' | null
  const [optionSheet, setOptionSheet] = useState(null); // 'SCHEDULE' | 'SEATS' | null

  const selectedDeparture = departureAt ? new Date(departureAt) : null;
  const dateLabel = selectedDeparture
    ? (selectedDeparture.toDateString() === new Date().toDateString() ? 'Hôm nay' : selectedDeparture.toLocaleDateString('vi-VN'))
    : null;
  const timeLabel = selectedDeparture ? selectedDeparture.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit', hour12: false }) : null;

  // Sync external pickup & dropoff
  React.useEffect(() => {
    if (externalPickup) {
      setPickup(externalPickup);
    }
  }, [externalPickup]);

  React.useEffect(() => {
    if (externalDropoff) {
      setDropoffObj(externalDropoff);
    }
  }, [externalDropoff]);

  const swapPlaces = () => {
    const oldDropoffObj = dropoffObj;
    const oldPickup = pickup;
    if (!oldDropoffObj) return;

    setDropoffObj(oldPickup);
    setPickup(oldDropoffObj);
  };

  const openWithKeyboard = (event, target) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    setEditingTarget(target);
  };

  const handleSchedule = () => {
    if (!pickup || !dropoffObj || !departureAt) return;
    // The app opens its scheduled-demand form here. It must not run the
    // marketplace search: a scheduled demand is published for drivers.
    onScheduleRide?.({
      pickup,
      dropoff: dropoffObj,
      seats: Number(rideSeats),
      departureAt,
      isCharter: false,
    });
  };

  const isButtonDisabled = !pickup || !dropoffObj || !departureAt || loading;

  return (
    <div className="panel search-card">
      <div className="eyebrow">ĐẶT CHUYẾN XE GHÉP</div>
      <h2>Bạn muốn đi đâu?</h2>
      <p className="help">Nhập điểm đến để bắt đầu hành trình của bạn.</p>

      <div className="routebox">
        {/* Row 1: Pickup */}
        <div
          className="route-row"
          onClick={() => setEditingTarget('PICKUP')}
          onKeyDown={(event) => openWithKeyboard(event, 'PICKUP')}
          role="button"
          tabIndex={0}
          aria-label="Chọn điểm đón"
        >
          <span className="dot" />
          <div style={{ flex: 1 }}>
            <span className="route-label">Điểm đón</span>
            <span className={`routeval ${!pickup ? 'route-placeholder' : ''}`} id="pickupLabel">
              {pickup?.address ? pickup.address.split(',')[0] : 'Chọn hoặc nhập điểm đón'}
            </span>
            {pickup && <span className={`badge ${pickup.lowAccuracy ? 'warning' : ''}`}>
              {pickup.lowAccuracy ? 'Vị trí có thể chưa chính xác' : 'Vị trí hiện tại'}
            </span>}
          </div>
        </div>

        {/* Row 2: Destination */}
        <div
          className="route-row"
          onClick={() => setEditingTarget('DROPOFF')}
          onKeyDown={(event) => openWithKeyboard(event, 'DROPOFF')}
          role="button"
          tabIndex={0}
          aria-label="Chọn điểm đến"
        >
          <span className="pin" />
          <div style={{ flex: 1 }}>
            <span className="route-label">Điểm đến</span>
            <span className={`routeval ${!dropoffObj ? 'route-placeholder' : ''}`}>
              {dropoffObj?.address ? dropoffObj.address.split(',')[0] : 'Bạn muốn đi đâu?'}
            </span>
          </div>
        </div>

        {/* Swap Button */}
        <button
          title="Hoán đổi điểm đón và điểm đến"
          className="swap"
          type="button"
          onClick={swapPlaces}
        >
          <RideAppIcon name="swap" />
        </button>
      </div>

      {editingTarget && <RidePlaceSearchSheet
        target={editingTarget}
        value={editingTarget === 'PICKUP' ? pickup : dropoffObj}
        currentLocation={externalPickup || pickup}
        onOpenMap={(target) => { setEditingTarget(null); onOpenMapPicker?.(target); }}
        onClose={() => setEditingTarget(null)}
        onSelect={(point) => {
          if (editingTarget === 'PICKUP') setPickup(point);
          else setDropoffObj(point);
          setEditingTarget(null);
        }}
      />}

      {optionSheet === 'SCHEDULE' && <RideScheduleSheet initialValue={departureAt} onClose={() => setOptionSheet(null)} onConfirm={(value) => { setDepartureAt(value); setOptionSheet(null); }} />}
      {optionSheet === 'SEATS' && <RideSeatSheet initialSeats={rideSeats} onClose={() => setOptionSheet(null)} onConfirm={(value) => { setRideSeats(value); setOptionSheet(null); }} />}

      {/* Date and pickup time share one selector, matching the mobile app. */}
      <div className="pickers">
        <button type="button" className="picker picker--schedule" onClick={() => setOptionSheet('SCHEDULE')}>
          <span className="picker-label">
            <RideAppIcon name="calendar" /> Ngày &amp; giờ đón
          </span>
          <strong className={!selectedDeparture ? 'picker-placeholder' : ''}>
            {selectedDeparture ? `${dateLabel} · ${timeLabel}` : 'Chọn thời điểm đón'}
          </strong><RideAppIcon name="arrowDown" />
        </button>

        <button type="button" className="picker picker--seats" onClick={() => setOptionSheet('SEATS')}>
          <span className="picker-label">
            <RideAppIcon name="people" /> Số ghế
          </span>
          <strong>{rideSeats} ghế</strong><RideAppIcon name="arrowDown" />
        </button>
      </div>

      {/* Action Button matching prototype */}
      <button
        id="bookingButton"
        className="btn fullbutton"
        type="button"
        disabled={isButtonDisabled}
        onClick={handleSchedule}
      >
        {loading ? (
          <>
            <RideAppIcon name="time" className="ride-icon-spin" /> Đang tìm chuyến...
          </>
        ) : (
          <>
            <RideAppIcon name="calendar" /> Đặt chuyến đi hẹn giờ
          </>
        )}
      </button>
    </div>
  );
};

export default RideSearchSection;
